import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Chip from "@mui/material/Chip";
import { History, ArrowRight } from "lucide-react";
import type { HealthEvent } from "@property-reports/api-contract";

interface HealthHistoryCardProps {
  events: HealthEvent[];
  loading?: boolean;
}

function formatDuration(ms: number | undefined): string {
  if (!ms) return "";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  return `${Math.round(ms / 3600000)}h`;
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
  return date.toLocaleDateString("en-NZ");
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "default"
> = {
  healthy: "success",
  pass: "success",
  degraded: "warning",
  warn: "warning",
  unhealthy: "error",
  fail: "error",
};

function StatusChip({ status }: { status: string }) {
  return (
    <Chip
      label={status}
      size="small"
      color={statusColors[status] || "default"}
      sx={{ height: 20, fontSize: "0.7rem" }}
    />
  );
}

export function HealthHistoryCard({ events, loading }: HealthHistoryCardProps) {
  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <History size={20} />
            <Typography variant="subtitle1" fontWeight={600}>
              Health History
            </Typography>
          </Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="text" height={40} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <History size={20} />
          <Typography variant="subtitle1" fontWeight={600}>
            Health History
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto" }}
          >
            Last {events.length} events
          </Typography>
        </Box>

        {events.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            No health events recorded yet
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {events.slice(0, 10).map((event, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  fontSize: "0.875rem",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 60 }}
                >
                  {formatTime(event.timestamp)}
                </Typography>

                {event.component ? (
                  // Component-level event
                  <>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, minWidth: 80 }}
                    >
                      {event.component}
                    </Typography>
                    {event.componentPreviousStatus && (
                      <>
                        <StatusChip status={event.componentPreviousStatus} />
                        <ArrowRight size={14} />
                      </>
                    )}
                    {event.componentNewStatus && (
                      <StatusChip status={event.componentNewStatus} />
                    )}
                  </>
                ) : (
                  // Overall status event
                  <>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, minWidth: 80 }}
                    >
                      System
                    </Typography>
                    {event.previousStatus && (
                      <>
                        <StatusChip status={event.previousStatus} />
                        <ArrowRight size={14} />
                      </>
                    )}
                    <StatusChip status={event.newStatus} />
                  </>
                )}

                {event.duration && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: "auto" }}
                  >
                    after {formatDuration(event.duration)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
